import propTypes from 'prop-types';
import {useEffect, useRef, useState, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import FastModal from '../../FastModal';
import {parseMessage} from '../../../lib/stringHelper';
import {getModalData} from '../../../stores/selectors/typedModalSelectors';
import {getIsTrayAttached} from '../../../stores/selectors/trayCalibrationSelectors';
import FormattedHTMLMessage from '../../FormattedHTMLMessage';
import {CommonButtonLabel} from '../../../constants/dataEnum';
import {fastRecovery} from '../../../stores/business/recoveryBusiness';
import {hideLoading, showLoading} from '../../../stores/actions/appStateAction';
import {getRealTimeErrorCodeNumber} from '../../../stores/selectors/modalStateSelectors';
import {checkAndSetIsTrayAttached, scanTrayIdWithTouchProbeExtended} from '../../../stores/business/trayBusiness';
import {clearModalError, hideModal} from '../../../stores/actions/modalStateAction';
import {getMissingTrayOngoingProcess, handlePostMissingTrayScanTrayId} from './missingTrayBusiness';
import {
  getSetupWizardCurrentStepNumber,
  isOnSetupWizardView,
} from '../../../stores/business/setupWizardBusiness/commonSetupWizardBusiness';
import {setTypedModalData} from '../../../stores/actions/typedModalAction';
import createTrayRequirementChecker from '../../../stores/business/TrayRequirementChecker';
import {fastCommonActuatorAPIErrorHandler} from '../../../stores/business/errorHandlerBusiness';

let trayInsertionTimeout;
const TRAY_INSERTION_TIMEOUT = 15000;

function ModalTrayStillNotDetected({processName, callback, errorNumber}) {
  const dispatch = useDispatch();

  const isOnSetupWizard = dispatch(isOnSetupWizardView());
  const stepWizardStepNumber = dispatch(getSetupWizardCurrentStepNumber());

  const specificProcessName = isOnSetupWizard
    ? parseMessage(`setupWizard.stepper.label.step${stepWizardStepNumber}`)
    : processName;

  const header = parseMessage('modal.missingTray.busy.stillNotDetected.title');
  const content = (
    <FormattedHTMLMessage
      id="modal.missingTray.busy.stillNotDetected.text"
      values={{processName: specificProcessName}}
    />
  );
  const button = [{onClick: callback, label: parseMessage(CommonButtonLabel.GOT_IT)}];

  return <FastModal show header={header} content={content} btnAction={button} errorCodeNumber={errorNumber} />;
}

ModalTrayStillNotDetected.propTypes = {
  processName: propTypes.string.isRequired,
  callback: propTypes.func.isRequired,
  errorNumber: propTypes.number.isRequired,
};

function ModalMissingTray() {
  const dispatch = useDispatch();
  const isTrayAttachedCaught = useRef(false);

  const [isTrayNotDetectedAfterRecovery, setIsTrayNotDetectedAfterRecovery] = useState(false);
  const [isShown, setIsShown] = useState(true);

  const data = useSelector(getModalData);
  const isTrayAttached = useSelector(getIsTrayAttached);
  const errorNumber = useSelector(getRealTimeErrorCodeNumber);

  const ongoingProcess = dispatch(getMissingTrayOngoingProcess());

  const {failedCallback, isNeedRecovery} = data || {};

  const onFailedTrayRecovery = () => {
    dispatch(hideLoading());
    dispatch(hideModal());
    if (failedCallback) failedCallback();
  };

  const recoveryAction = async () => {
    setIsShown(false);
    await dispatch(fastRecovery());
    dispatch(showLoading(parseMessage('modal.missingTray.loading.waitingForTray')));

    // waiting for tray to be installed
    trayInsertionTimeout = setTimeout(async () => {
      const isTrayAttachedByInstrument = await dispatch(checkAndSetIsTrayAttached());

      if (isTrayAttachedByInstrument) {
        doPostTrayAttachmentAction();
        return;
      }

      dispatch(hideLoading());
      setIsTrayNotDetectedAfterRecovery(true);
    }, TRAY_INSERTION_TIMEOUT);
  };

  const doPostTrayAttachmentAction = useCallback(async () => {
    clearTimeout(trayInsertionTimeout);
    isTrayAttachedCaught.current = true;

    const permissionChecker = await dispatch(createTrayRequirementChecker());

    dispatch(clearModalError());
    dispatch(setTypedModalData({...data, permissionChecker}));

    // if the touch probe is extended, the scanning tray Id sequence is handled manually by UI
    //
    // if it is retracted, the instrument will automatically scan the tray id.
    // UI just need to wait for trayAttachmentHandlingDone event to arrived and then execute the handlePostMissingTrayScanTrayId func

    // TODO: need BE to uniformly gives event after the scanning sequence is finished and tray is out so there wouldn't be 2 separate flow for post missing tray action
    if (permissionChecker.isTouchProbeInitiallyExtended) {
      const isShowRemoveLabwareConfirmation = permissionChecker.isDoingPlateDefinition();

      // waiting for instrument to release its busy status first
      setTimeout(() => {
        dispatch(hideLoading());
        dispatch(
          scanTrayIdWithTouchProbeExtended(isShowRemoveLabwareConfirmation, fastCommonActuatorAPIErrorHandler, () =>
            dispatch(handlePostMissingTrayScanTrayId()),
          ),
        );
      }, 4000);
    }
  }, [data, dispatch]);

  const processName = isNeedRecovery ? parseMessage(`modal.missingTray.busy.process.${ongoingProcess}`) : null;

  const buttonRecover = [{label: parseMessage(CommonButtonLabel.RECOVER), onClick: recoveryAction}];
  const buttonGotIt = [{label: parseMessage(CommonButtonLabel.GOT_IT), onClick: onFailedTrayRecovery}];
  const button = isNeedRecovery ? buttonRecover : buttonGotIt;

  const header = parseMessage('modal.missingTray.title');
  const content = isNeedRecovery ? (
    <FormattedHTMLMessage id="modal.missingTray.busy.text" values={{processName}} />
  ) : (
    <FormattedHTMLMessage id="modal.missingTray.idle.text" values={{processName}} />
  );

  useEffect(() => {
    // check for tray Id. if tray Id present, then hide modal instantly and cleared the tray insertion timeout
    if (isTrayAttached && !isTrayNotDetectedAfterRecovery && !isTrayAttachedCaught.current && isNeedRecovery) {
      doPostTrayAttachmentAction();
    }
  }, [doPostTrayAttachmentAction, isTrayNotDetectedAfterRecovery, isTrayAttached, isNeedRecovery]);

  return isTrayNotDetectedAfterRecovery ? (
    <ModalTrayStillNotDetected processName={processName} callback={onFailedTrayRecovery} errorNumber={errorNumber} />
  ) : (
    <FastModal show={isShown} header={header} content={content} errorCodeNumber={errorNumber} btnAction={button} />
  );
}

export default ModalMissingTray;
